import { useT } from '@kmgestao/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kmgestao/ui';

import { useDeleteProvider, type ProviderRow } from '../../../../features/ai-admin/index.js';

export interface DeleteProviderDialogProps {
  provider: ProviderRow | null;
  onClose: () => void;
}

export function DeleteProviderDialog({ provider, onClose }: DeleteProviderDialogProps) {
  const { t } = useT('ai-admin');
  const { t: tCommon } = useT('common');
  const del = useDeleteProvider();
  const open = provider !== null;

  const handleConfirm = async () => {
    if (!provider) return;
    try {
      await del.mutateAsync({ providerId: provider.id });
      toast.success(t('providerDelete.success'));
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('provider_in_use')) {
        toast.error(t('providerDelete.error.inUse'));
      } else {
        toast.error(t('providerDelete.error.generic'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('providerDelete.title')}</DialogTitle>
          <DialogDescription>{t('providerDelete.description')}</DialogDescription>
        </DialogHeader>

        {provider ? (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            <div className="font-medium">{provider.displayName}</div>
            {provider.primaryModel ? (
              <div className="text-xs text-muted-foreground font-mono">
                {provider.primaryModel.modelId}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {tCommon('actions.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={del.isPending}
          >
            {del.isPending ? tCommon('status.loading') : t('providerDelete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
